import { type CSSProperties, type JSX, memo, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import { Line, LineChart, XAxis, YAxis } from "recharts";
import type { RenderableText } from "recharts/types/component/Text";
import { chartColor } from "../../colors";
import { domainNticks, fmtTick } from "./MilesLineChart";
import { Legend, LegendLabel, LegendLine } from "./stylings";

export type IChartDatum =
    | {
          label: number;
      }
    | Record<string, number>;

type XAxisType = "day" | "week" | "month";
type RelativeType = "calendar" | "path";

export interface IProps {
    chartData: IChartDatum[];
    chartRef?: RefObject<HTMLDivElement>;
    state: {
        xAxis: XAxisType;
        relative: RelativeType;
        maxTicks: number;
        dataLabels: boolean;
    };
    tasks: {
        id: string;
        name: string;
    }[];
    numMembers: number;
    style?: CSSProperties;
}

interface ILabelProps {
    x?: string | number;
    y?: string | number;
    stroke?: string;
    value?: RenderableText;
    index?: number;
}

function renderLabel(
    props: ILabelProps,
    ticks: Set<number>,
    chartData: IChartDatum[],
    fontSize: number,
): JSX.Element | null {
    const { x, y, stroke, value, index } = props;
    if (!value || index === undefined) {
        return null;
    }
    const datum = chartData[index];
    if (!ticks.has(datum.label)) {
        return null;
    }
    return (
        <text x={x} y={y} dy={-4} dx={1} fill={stroke} fontSize={fontSize} textAnchor="end">
            {value}
        </text>
    );
}

const margins = { top: 15, right: 5, bottom: 5, left: 5 };

function TaskCountLineChart({ chartData, chartRef, state, tasks, numMembers, style }: IProps): JSX.Element {
    const localRef = useRef<HTMLDivElement>(null);
    const resolvedRef = chartRef ?? localRef;
    const [labelFontSize, setLabelFontSize] = useState(15);
    const { xAxis, relative, dataLabels, maxTicks } = state;
    const type = xAxis === "day" ? "stepAfter" : "linear";
    const [domain, ticks] = domainNticks(chartData, maxTicks);
    const tickSet = useMemo(() => new Set(ticks), [ticks]);
    const axisFontSize = labelFontSize + 1;

    useEffect(() => {
        const element = resolvedRef.current;
        if (!element) {
            return;
        }

        const updateFontSize = (width: number) => {
            if (width < 420) {
                setLabelFontSize(11);
            } else if (width < 520) {
                setLabelFontSize(12);
            } else if (width < 680) {
                setLabelFontSize(13);
            } else {
                setLabelFontSize(15);
            }
        };

        updateFontSize(element.getBoundingClientRect().width);
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) {
                return;
            }
            updateFontSize(entry.contentRect.width);
        });
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [resolvedRef]);

    return (
        <div style={style} ref={resolvedRef}>
            <div style={{ width: "100%", aspectRatio: "2 / 1" }}>
                <LineChart data={chartData} margin={margins} width="100%" height="100%" responsive>
                    <YAxis type="number" domain={[0, numMembers]} tick={{ fontSize: axisFontSize }} />
                    <XAxis
                        dataKey="label"
                        type="number"
                        domain={domain}
                        ticks={ticks}
                        tickFormatter={(val) => fmtTick(val, xAxis, relative)}
                        minTickGap={2}
                        tick={{ fontSize: axisFontSize }}
                    />
                    {tasks.map((task, idx) => (
                        <Line
                            key={task.id}
                            isAnimationActive={false}
                            dataKey={task.id}
                            stroke={chartColor(idx)}
                            type={type}
                            strokeWidth={2}
                            dot={false}
                            label={dataLabels && ((props) => renderLabel(props, tickSet, chartData, labelFontSize))}
                        />
                    ))}
                </LineChart>
            </div>
            <Legend>
                {tasks.map((task, idx) => (
                    <LegendLabel key={task.id}>
                        <LegendLine $color={chartColor(idx)} />
                        {task.name}
                    </LegendLabel>
                ))}
            </Legend>
        </div>
    );
}

export default memo(TaskCountLineChart);
